import { percentageFormatter } from "@/app/percentage/utils"
import { CryptoAndUsdPrices, CryptoAndUsdPricesSkeleton } from "@/app/price"
import { Action } from "@/app/profiles/bids/table/action"
import { HeaderCell } from "@/app/profiles/bids/table/cells"
import {
  BidEntity,
  MiniatureSkeleton,
} from "@/app/profiles/bids/table/miniature"
import type { BidItem } from "@anaconda/core/bid/types"
import { Trans } from "@lingui/react/macro"
import { Skeleton, cn } from "@rarible_int/anaconda_ui_components"
import Big from "big.js"

interface Column {
  Header: React.ComponentType
  Content: React.ComponentType<BidItem>
  Skeleton: React.ComponentType
}

/**
 * Columns for the collection-scoped offer sub-tabs.
 *
 * Deliberately NOT the profile bids columns: those carry a Floor Price column,
 * which is the *collection's* floor and is therefore identical on every row
 * here — the collection header already shows it once. What varies per row, and
 * is what a seller actually reads, is how each offer compares to that floor.
 */
export const BIDS_COLUMNS: Column[] = [
  {
    Header: () => (
      <HeaderCell className="w-[250px] md:w-[350px]">
        <Trans>Collection / Item</Trans>
      </HeaderCell>
    ),
    Content: (bidItem) => <BidEntity bid={bidItem} />,
    Skeleton: MiniatureSkeleton,
  },
  {
    Header: () => (
      <HeaderCell className="w-[150px] md:w-[180px]">
        <Trans>Offer Price</Trans>
      </HeaderCell>
    ),
    Content: (bidItem) => <CryptoAndUsdPrices {...bidItem.bid.price} />,
    Skeleton: () => <CryptoAndUsdPricesSkeleton />,
  },
  {
    Header: () => (
      <HeaderCell className="w-[120px] md:w-[140px]">
        <Trans>vs Floor</Trans>
      </HeaderCell>
    ),
    Content: (bidItem) => <FloorDifference bidItem={bidItem} />,
    Skeleton: () => (
      <div className="flex justify-end">
        <Skeleton className="h-6 w-[60px]" />
      </div>
    ),
  },
  {
    Header: () => (
      <HeaderCell className="w-[150px] md:w-[180px]">
        <Trans>Actions</Trans>
      </HeaderCell>
    ),
    Content: (bidItem) => (
      <div className="flex justify-end">
        <Action bidItem={bidItem} />
      </div>
    ),
    Skeleton: () => (
      <div className="flex justify-end">
        <Skeleton className="h-9 w-[75px]" />
      </div>
    ),
  },
]

function FloorDifference({ bidItem }: { bidItem: BidItem }) {
  const floor = bidItem.collectionFloorPrice

  if (!floor) return <span>—</span>

  let percentage: number
  try {
    const floorAmount = Big(floor.amount)
    if (floorAmount.lte(0)) return <span>—</span>

    percentage = Big(bidItem.bid.price.amount)
      .minus(floorAmount)
      .div(floorAmount)
      .times(100)
      .toNumber()
  } catch {
    return <span>—</span>
  }

  const rounded = Number(percentageFormatter.format(Math.abs(percentage)))

  if (rounded === 0) return <span className="text-foreground/60">Floor</span>

  return (
    <span className={cn(percentage > 0 ? "text-success" : "text-destructive")}>
      {percentage > 0 ? "+" : "−"}
      {percentageFormatter.format(Math.abs(percentage))}%
    </span>
  )
}
